// src/pages/Home.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import logo from '../assets/SamsoftixLogo.jpg';
import './Home.css';
import db from '../firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';

function Home() {
  // Contact form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  // Newsletter simple state
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Common submit handler (contact + newsletter)
  const handleContactSubmit = async (e, type = 'contact') => {
    e.preventDefault();
    // Basic validation
    if (type === 'contact') {
      if (!name.trim() || !email.trim() || !message.trim()) {
        alert('Please fill in all required fields.');
        return;
      }
    } else {
      if (!newsletterEmail.trim()) {
        alert('Please enter your email to subscribe.');
        return;
      }
    }

    setSubmitting(true);
    try {
      if (type === 'contact') {
        await addDoc(collection(db, 'contacts'), {
          name,
          email,
          phone,
          message,
          type: 'contact',
          createdAt: Timestamp.now(),
        });
        alert('Message sent successfully!');
        setName('');
        setEmail('');
        setPhone('');
        setMessage('');
      } else {
        await addDoc(collection(db, 'newsletter'), {
          email: newsletterEmail,
          type: 'newsletter',
          createdAt: Timestamp.now(),
        });
        alert('Subscribed to newsletter — thank you!');
        setNewsletterEmail('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="home">
      <Helmet>
        <title>
          SamSoftix | Web, Mobile, Game & AI Development + Remote & On-site Training
        </title>

        <meta
          name="description"
          content="SamSoftix (head office Wan) provides professional website, mobile app, game, ecommerce and AI/ML development. We offer onsite & remote coding training for learners in Sambrial, Sialkot and nearby areas — practical courses, real projects, and placement support."
        />

        <meta
          name="keywords"
          content="SamSoftix, Wan, Sambrial, web development, mobile app development, game development, AI ML, ecommerce, coding training, remote classes, learn programming, web development course Sambrial, app development course Sialkot"
        />
        <meta name="author" content="SamSoftix" />
        <link rel="canonical" href="https://www.samsoftix.com/" />

        {/* Open Graph */}
        <meta property="og:title" content="SamSoftix | Web, App, Game & AI Development in Wan & Sambrial" />
        <meta
          property="og:description"
          content="SamSoftix develops websites, mobile apps, games and AI systems — and trains learners both onsite and remotely. Head Office in Wan, serving Sambrial and nearby cities."
        />
        <meta property="og:image" content="https://www.samsoftix.com/assets/SamsoftixLogo.jpg" />
        <meta property="og:url" content="https://www.samsoftix.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_PK" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SamSoftix | Software & Training Center" />
        <meta name="twitter:description" content="Web, mobile, game & AI development plus remote & onsite training classes." />
        <meta name="twitter:image" content="https://www.samsoftix.com/assets/SamsoftixLogo.jpg" />
        <meta name="twitter:site" content="@samsoftix" />

        {/* Structured data: LocalBusiness + Services + Courses + FAQ */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "SamSoftix",
            image: "https://www.samsoftix.com/assets/SamsoftixLogo.jpg",
            url: "https://www.samsoftix.com",
            telephone: "+92-332-0472184",
            email: "info@samsoftix.com",
            description:
              "SamSoftix is a software house and training center offering web, mobile, game, ecommerce and AI/ML development services, plus onsite & remote training programs for learners.",
            address: [
              {
                "@type": "PostalAddress",
                streetAddress: "Wan, Sambrial",
                addressLocality: "Wan",
                addressRegion: "Punjab",
                postalCode: "51010",
                addressCountry: "PK",
              }
            ],
            sameAs: [
              "https://facebook.com/samsoftix",
              "https://linkedin.com/company/samsoftix",
              "https://instagram.com/samsoftix",
            ],
            areaServed: ["Wan", "Sambrial","China","International Clients","Dubai","USA","UK","SingaPur"],
            serviceOffered: [
              { "@type": "Service", name: "Website Development" },
              { "@type": "Service", name: "Mobile App Development" },
              { "@type": "Service", name: "Game Development" },
              { "@type": "Service", name: "Ecommerce Development" },
              { "@type": "Service", name: "AI & ML Solutions" },
              { "@type": "Service", name: "Custom Software Solutions" },
              { "@type": "Service", name: "SEO & Digital Marketing" },
              { "@type": "Service", name: "Onsite & Remote Training" },
              { "@type": "Service", name: "LMS Systems" },
              { "@type": "Service", name: "SaaS" },
              { "@type": "Service", name: "ChatBots" },
              { "@type": "Service", name: "Data Cleaning" },
              { "@type": "Service", name: "Website Deployment" },


            ],
            hasCourse: [
              {
                "@type": "Course",
                name: "Web Development Bootcamp - Beginner to Professional",
                description:
                  "Hands-on course covering HTML, CSS, JavaScript, React, Node.js, databases, deployment, and SEO with real projects.",
                provider: { "@type": "Organization", name: "SamSoftix" },
              },
              {
                "@type": "Course",
                name: "Mobile App Development - React Native & Flutter",
                description:
                  "Cross-platform mobile app course covering React Native and Flutter with real projects and publishing guidance.",
                provider: { "@type": "Organization", name: "SamSoftix" },
              },
              {
                "@type": "Course",
                name: "Game Development with Unity & Phaser",
                description:
                  "2D & 3D game development fundamentals, asset pipelines, monetization, and publishing on Android and Web.",
                provider: { "@type": "Organization", name: "SamSoftix" },
              },
            ],
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Where are SamSoftix offices?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "SamSoftix head office is in Wan, Sambrial. We also operate in Sambrial (Moon Plaza) — both addresses are used for local listings and training.",
                },
              },
              {
                "@type": "Question",
                name: "Do you offer remote classes?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. SamSoftix offers remote (online) classes for web, mobile, game, and AI development — join from anywhere in Pakistan.",
                },
              },
              {
                "@type": "Question",
                name: "What courses do you offer?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We offer Web Development, Mobile App Development (React Native & Flutter), Game Development (Unity), AI/ML training, and custom short-courses for businesses.",
                },
              },
              {
                "@type": "Question",
                name: "How do I enroll?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Use the contact form below or WhatsApp us at 0332-0472184 to request batch details, schedules, and enrollment process.",
                },
              },
            ],
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <motion.section
        className="hero"
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
      >
        <div className="hero-left">
          <img src={logo} alt="SamSoftix Logo" className="hero-logo" loading="lazy" />
        </div>

        <div className="hero-right">
          <h1>SamSoftix Software House in Sambrial & Sialkot for Web, Mobile App, Game & AI Development</h1>
          <p className="lead">
            <strong>SamSoftix</strong> is a leading <strong>software house and website development company in Sambrial and Sialkot</strong>
            providing <strong>web development</strong>, <strong>ecommerce solutions</strong>, <strong>mobile app development</strong>,
            <strong>game development</strong>, and <strong>AI/ML services</strong> for <strong>businesses across Pakistan and international clients</strong>.
            We also offer <strong>onsite and remote web & app development courses</strong> for students who want to become professional developers through practical, project-based training.
          </p>

          <ul className="hero-quicklist">
            <li>✅ Head Office: <strong>Wan, Sambrial, Punjab, Pakistan</strong> serving Sialkot and nearby cities</li>
            <li>✅ <strong>Website & Web Development Company</strong> for businesses in Pakistan and international clients</li>
            <li>✅ Services: <strong>Web Development, Mobile App Development, Game Development, Ecommerce, AI/ML, SEO</strong></li>
            <li>✅ Training: <strong>Web & App Development Courses</strong>, Bootcamps, Short Courses, Corporate Training (Onsite & Remote)</li>
          </ul>

          <div className="hero-ctas">
            <Link to="/contact" className="cta-primary">Get Free Consultation</Link>
            <a href="#courses" className="cta-secondary">View Courses & Batches</a>
          </div>
        </div>
      </motion.section>

      {/* SERVICES / BENEFITS */}
      <motion.section
        className="services"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
        id="services"
      >
        <h2>Professional Web, Mobile App, Game & AI Development Services in Sambrial and Sialkot</h2>
        <div className="services-grid">
          <article className="card">
            <h3>Website Development Company</h3>
            <p>
              We create <strong>fast, responsive, and SEO-friendly websites</strong> using React, Next.js, or custom solutions designed to attract more visitors, convert leads, and grow your business in Sambrial, Sialkot, Pakistan, and internationally.
            </p>
            <ul>
              <li>Business & corporate websites</li>
              <li>Landing pages & lead generation funnels</li>
              <li>CMS, Headless CMS, and fully custom web solutions</li>
            </ul>
          </article>

          <article className="card">
            <h3>Mobile App Development Company</h3>
            <p>
              We build <strong>cross-platform mobile apps</strong> using <strong>React Native</strong> and <strong>Flutter</strong> for iOS and Android designed to engage users, increase conversions, and grow businesses in Sambrial, Sialkot, Pakistan, and globally. We also handle publishing to the Play Store and App Store.
            </p>
            <ul>
              <li>Consumer mobile apps for Android & iOS</li>
              <li>Enterprise apps & admin dashboards</li>
              <li>Push notifications, analytics, and performance optimization</li>
            </ul>
          </article>


          <article className="card">
            <h3>Game Development Company</h3>
            <p>
              We create <strong>2D & 3D games</strong> using <strong>Unity</strong> and <strong>Phaser</strong> for mobile, web, and desktop including monetization, ad integration, and publishing support. Our games engage users, educate audiences, and help businesses grow in Sambrial, Sialkot, Pakistan, and internationally.
            </p>
            <ul>
              <li>Casual mobile games for Android & iOS</li>
              <li>Educational games & gamified apps for learning</li>
              <li>Web-based mini-games & interactive experiences</li>
            </ul>
          </article>


          <article className="card">
            <h3>AI & Machine Learning Solutions</h3>
            <p>
              We provide <strong>custom AI and machine learning solutions</strong> for web, mobile, and enterprise applications including model training, automation, and intelligent features to boost efficiency and business growth in Sambrial, Sialkot, Pakistan, and for international clients.
            </p>
            <ul>
              <li>AI-powered features for apps and websites</li>
              <li>Data analysis, dashboards, and predictive insights</li>
              <li>Custom ML model training, deployment, and optimization</li>
            </ul>
          </article>


          <article className="card">
            <h3>Ecommerce & Automation Solutions</h3>
            <p>
              We build <strong>custom ecommerce platforms</strong> using Shopify, WooCommerce, or fully custom solutions managing payments, inventory, shipping, and automation. Our solutions help businesses in Sambrial, Sialkot, across Pakistan, and international clients grow online and streamline operations.
            </p>
          </article>


          <article className="card">
            <h3>Web, Mobile, Game, AI & Office Training Courses</h3>
            <p>
              Practical, project-based training in web development, mobile apps, game development, AI/ML, ecommerce, and Microsoft Office. Join <strong>onsite classes in Sambrial & Sialkot</strong> or <strong>live remote sessions</strong> from anywhere in Pakistan or internationally.
            </p>
            <ul>
              <li>Web Development (React, Node.js, SEO)</li>
              <li>Mobile Apps (React Native, Flutter, iOS & Android)</li>
              <li>Game Development (Unity & Phaser)</li>
              <li>AI & Machine Learning</li>
              <li>Ecommerce Platforms (Shopify, WooCommerce, Custom)</li>
              <li>Microsoft Office & Office Management</li>
            </ul>
          </article>


        </div>
      </motion.section>

      {/* COURSES */}
      <motion.section
        className="courses"
        id="courses"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
      >
        <h2>Practical Web, Mobile, Game, AI & Office Training Courses in Sambrial, Sialkot & Online</h2>


        <div className="course-grid">
          <div className="course-card">
            <h3>Web Development Bootcamp (12 Weeks, Part-Time)</h3>
            <p>
              Learn <strong>HTML, CSS, JavaScript, React, Node.js, databases, deployment, and SEO</strong> while building 3 real projects. Gain practical skills to launch your career in web development.
            </p>
            <p><strong>Who it's for:</strong> Beginners, career switchers, and aspiring web developers.</p>
            <ul>
              <li>Hands-on curriculum: React, Node, MongoDB/Postgres, Git, Hosting</li>
              <li>Live sessions + recorded materials for flexible learning</li>
              <li>Placement support, portfolio review, and career guidance</li>
            </ul>
          </div>


          <div className="course-card">
            <h3>Mobile App Development Bootcamp (13 Weeks)</h3>
            <p>
              Learn <strong>React Native & Flutter</strong> to build cross-platform mobile apps for iOS and Android. Gain hands-on experience with native APIs, app testing, and deployment to app stores.
            </p>
            <ul>
              <li>Publish apps to Google Play Store & Apple App Store</li>
              <li>Integrate push notifications and optimize app performance</li>
              <li>Build real projects to include in your portfolio</li>
            </ul>
          </div>


          <div className="course-card">
            <h3>Game Development Bootcamp (16 Weeks)</h3>
            <p>
              Learn <strong>2D & 3D game development</strong> using <strong>Unity</strong> and <strong>Phaser</strong>. Gain hands-on experience in game mechanics, asset creation, monetization, and publishing for web, mobile, and desktop platforms.
            </p>
            <ul>
              <li>2D & 3D game pipelines and design fundamentals</li>
              <li>Ad integration, in-app purchases, and monetization strategies</li>
              <li>Build playable projects for your portfolio</li>
            </ul>
          </div>


          <div className="course-card">
            <h3>AI & Machine Learning Bootcamp (10 Weeks)</h3>
            <p>
              Gain hands-on experience in <strong>AI and machine learning</strong> from data pipelines and model training to deployment and real-world business applications. Learn to integrate ML into web and mobile apps for practical projects.
            </p>
            <ul>
              <li>Train, evaluate, and optimize machine learning models</li>
              <li>Integrate AI/ML solutions into web and mobile applications</li>
              <li>Work on real-world projects to build your portfolio</li>
            </ul>
          </div>
          <div className="course-card">
            <h3>Ecommerce Development Bootcamp (6–14 Weeks)</h3>
            <p>
              Learn to build <strong>custom ecommerce platforms</strong> using Shopify, WooCommerce, or fully custom solutions. Manage payments, inventory, shipping, and automation while gaining practical experience with real projects.
            </p>
            <ul>
              <li>Create professional online stores with Shopify, WooCommerce, or custom code</li>
              <li>Integrate payments, inventory management, and shipping solutions</li>
              <li>Optimize stores for SEO, performance, and conversions</li>
            </ul>
          </div>

          <div className="course-card">
            <h3>Microsoft Office & Office Management Training (6–14 Weeks)</h3>
            <p>
              Master essential <strong>Microsoft Office tools</strong> (Word, Excel, PowerPoint, Outlook) and develop professional office management skills. Learn project coordination, workflow optimization, and client communication for local and international businesses.
            </p>
            <ul>
              <li>Microsoft Word: Documents, templates, and reports</li>
              <li>Microsoft Excel: Formulas, data analysis, charts, and automation</li>
              <li>Microsoft PowerPoint & Outlook: Presentations, scheduling, and communication</li>
              <li>Office Management: Project coordination, workflow, and client support</li>
            </ul>
          </div>


        </div>

        <div className="courses-cta">
          <p>New batches forming limited seats. Join remote or on-site.</p>
          <Link to="/contact" className="cta-primary">Request Batch Schedule</Link>
        </div>
      </motion.section>

      {/* PORTFOLIO / CASE STUDIES (short) */}
      <motion.section className="portfolio" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.7 }}>
        <h2>Selected Projects & Case Studies</h2>
        <div className="portfolio-grid">
          <div className="portfolio-item">
            <h4>Local Ecommerce Store (UAE)</h4>
            <p>Complete ecommerce platform with secure payments, admin dashboard & inventory system. Increased online sales by 40% within 3 months for a Dubai-based retailer.</p>
          </div>

          <div className="portfolio-item">
            <h4>Anasim Fragrance – Ecommerce Brand (Pakistan)</h4>
            <p>Developed full ecommerce store for Anasim Fragrance with product management, secure checkout & marketing optimization, boosting nationwide brand visibility.</p>
          </div>

          <div className="portfolio-item">
            <h4>Service Provider Platform – Dubai</h4>
            <p>Multi-service booking website for plumbers, painters, AC technicians & maintenance companies with online booking, service tracking & CRM integration.</p>
          </div>

          <div className="portfolio-item">
            <h4>Food Delivery App</h4>
            <p>Cross-platform mobile application with live tracking, driver dashboard, restaurant management panel & real-time analytics.</p>
          </div>

          <div className="portfolio-item">
            <h4>Educational Management System – China</h4>
            <p>Student management portal for educational institutes in China including attendance, grading system & parent-teacher communication module.</p>
          </div>

          <div className="portfolio-item">
            <h4>School Website & LMS – Saudi Arabia</h4>
            <p>Modern responsive school website with integrated Learning Management System (LMS) for Saudi-based educational clients.</p>
          </div>

          <div className="portfolio-item">
            <h4>Educational Game (Web & Android)</h4>
            <p>Gamified learning application designed for classroom use with quizzes, performance tracking & interactive lessons.</p>
          </div>

          <div className="portfolio-item">
            <h4>Home Maintenance Booking App – UAE</h4>
            <p>Mobile app solution for home services including AC repair, plumbing & painting with real-time booking & payment gateway integration.</p>
          </div>

          <div className="portfolio-item">
            <h4>Corporate Business Website – Middle East Client</h4>
            <p>SEO-optimized business website for a Saudi-based company with multilingual support, lead capture forms & digital marketing setup.</p>
          </div>

        </div>
        <div className="portfolio-cta">
          <Link to="/contact" className="cta-secondary">See more case studies</Link>
        </div>
      </motion.section>

      {/* TESTIMONIALS */}
      <motion.section className="testimonials" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.7 }}>
        <h2>What Clients & Students Say</h2>
        <div className="testimonials-grid">
          <blockquote className="testimonial">
            <p>
              "SamSoftix built our ecommerce store <strong><a href="https://anasimfragrance.com/" target="_blank" rel="noreferrer">anasimfragrances.com</a></strong> and handled everything fast, professional, and the sales improved quickly."
            </p>
            <cite>Karachi, Pakistan</cite>
          </blockquote>

          <blockquote className="testimonial">
            <p>
              "SamSoftix developed our learning platform <strong><a href="https://myeduflow.eu" target="_blank" rel="noreferrer">MyEduFlow.eu</a></strong> with full dashboards for students and teachers the project was delivered on time and exceeded expectations."
            </p>
            <cite>Beijing China</cite>
          </blockquote>

          <blockquote className="testimonial">
            <p>
              "SamSoftix created our professional website <strong><a href="https://alabarajalsamia.com" target="_blank" rel="noreferrer">Alabarajalsamia.com</a></strong> the design, functionality, and SEO optimization were top-notch, helping us attract more clients."
            </p>
            <cite>Business Owner, Saudi Arabia</cite>
          </blockquote>

          <blockquote className="testimonial">
            <p>"I joined the remote bootcamp and got a job within 2 months after finishing the portfolio project."</p>
            <cite>Student, Sambrial</cite>
          </blockquote>
        </div>
      </motion.section>

      {/* WHY CHOOSE US */}
      <motion.section className="why-choose" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.7 }}>
        <h2>Why Choose SamSoftix for Web, Mobile, Game & AI Development?</h2>
        <ul>
          <li>
            <strong>Local & Remote Support:</strong> Onsite assistance at our Wan office plus live remote classes for learners across Pakistan and internationally.
          </li>
          <li>
            <strong>Practical, Project-Based Training:</strong> Hands-on courses where students build real-world projects and portfolio-ready applications.
          </li>
          <li>
            <strong>Comprehensive Full-Stack Services:</strong> From idea and design to development, deployment, SEO, and digital marketing — we handle it all for businesses and individuals.
          </li>
          <li>
            <strong>Career & Placement Support:</strong> Guidance on CVs, portfolios, and job placement for students completing bootcamps and training programs.
          </li>
          <li>
            <strong>Trusted by Clients:</strong> Real projects for businesses like <a href="https://myeduflow.eu" target="_blank" rel="noreferrer">MyEduFlow.eu</a>, <a href="https://gamehub.samsoftix.com" target="_blank" rel="noreferrer">Gamehub.Samsoftix.com</a>, <a href="https://anasimfragrance.com" target="_blank" rel="noreferrer">ANASIM Fragrances</a>, and <a href="https://alabarajalsamia.com" target="_blank" rel="noreferrer">Alabarajalsamia.com</a> etc.
          </li>
        </ul>
      </motion.section>

      <motion.section
        className="contact-map-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
      >
        <div className="contact-map-grid" style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>

          {/* LEFT SIDE: Office + FAQ */}
          <div className="contact-left" style={{ flex: '1 1 400px' }}>
            <h2>Our Head Office & Contact Information</h2>
            <p><strong>SamSoftix Wan, Sambrial, Punjab, Pakistan</strong></p>
            <p>Providing professional software development and training services to clients and learners across Pakistan and worldwide, with a focus on Dubai and the USA for international projects.</p>

            <p>
              WhatsApp: <a href="https://wa.me/923320472184">0332-0472184 </a>
              Email: <a href="mailto:samsoftix@gmail.com">samsoftix@gmail.com</a>
            </p>
            <p>Office Hours: Monday to Saturday, 10:00 AM – 6:00 PM</p>

            <h2>Frequently Asked Questions (FAQ)</h2>
            <div className="faq-item">
              <h4>Do you provide remote classes?</h4>
              <p>Yes! Live online classes with recordings, assignments, and mentor support.</p>
            </div>
            <div className="faq-item">
              <h4>How long are the courses?</h4>
              <p>Bootcamps last 8–12 weeks depending on course and pace. Part-time & weekend options available.</p>
            </div>
            <div className="faq-item">
              <h4>Can beginners join?</h4>
              <p>Yes! Courses start from fundamentals and progress to real-world projects.</p>
            </div>
            <div className="faq-item">
              <h4>Recent projects:</h4>
              <p>
                <a href="https://myeduflow.eu" target="_blank" rel="noreferrer">MyEduFlow.eu</a>,
                <a href="https://anasimfragrances.com" target="_blank" rel="noreferrer"> ANASIM Fragrances</a>,
                <a href="https://alabarajalsamia.com" target="_blank" rel="noreferrer"> Alabarajalsamia.com</a>,
                <a href="https://gamehub.samsoftix.com" target="_blank" rel="noreferrer"> GameHub.SamSoftix.com</a>
              </p>
            </div>
          </div>

          {/* RIGHT SIDE: Map */}
          <div className="contact-right" style={{ flex: '1 1 400px', minHeight: '400px' }}>
            <iframe
              title="SamSoftix Head Office Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3367.6644686715035!2d74.2979536!3d32.4281447!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391edf4d97cfdcad%3A0x11527b6087a2adbc!2sSamSoftix!5e0!3m2!1sen!2s!4v1769507979816!5m2!1sen!2s"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>

        </div>
      </motion.section>


      {/* CONTACT + CONTACT FORM (existing) */}
      <motion.section
        className="contact"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
      >
        <h2>Contact & Enrollment</h2>
        <p>
          Ready to start a project or enroll in a course? Send us a message we respond quickly via WhatsApp, email, or international inquiries from Dubai, the USA, and worldwide.
        </p>

        <form className="contact-form" onSubmit={(e) => handleContactSubmit(e, 'contact')}>
          <input
            type="text"
            placeholder="Your Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <input
            type="email"
            placeholder="Your Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Your Phone Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <textarea
            placeholder="Your Message (project or course enquiry)"
            rows="5"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
          ></textarea>
          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>

        {/* Newsletter */}
        <div className="newsletter">
          <h3>Send Message for Batch Alerts, Discounts & Updates</h3>
        </div>
      </motion.section>

      {/* FOOTER CTA */}
      <motion.footer
        className="footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
      >
        <div className="footer-top">
          <p>
            © {new Date().getFullYear()} <strong>SamSoftix</strong>  Head Office: Wan, Sambrial, Punjab, Pakistan
          </p>
          <p>
            Email: <a href="mailto:samsoftix@gmail.com">samsoftix@gmail.com</a> • <a href="mailto:info@samsoftix.com">info@samsoftix.com</a>
          </p>
          <p>
            Follow us:
            <a href="https://facebook.com/samsoftix" target="_blank" rel="noreferrer">Facebook </a> •
            <a href="https://linkedin.com/company/samsoftix" target="_blank" rel="noreferrer">LinkedIn </a>
          </p>
        </div>

        <div className="footer-bottom">
          <p>
            Serving clients across Pakistan and internationally, with <strong>Dubai</strong> <strong>China</strong>  <strong>SingaPur</strong> and the <strong>USA</strong> as preferred locations.
          </p>
        </div>
      </motion.footer>


    </div>
  );
}

export default Home;
